import React from "react";
import {css, cx} from "@panda/css";

import {CalendarCheckIcon, CloseIcon, MapMarkerOutlineIcon, PriceIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {rpAppLink} from "@pg-mono/rp-routes";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {usePrimaryClick} from "../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {Country} from "../../region/types/Country";
import {sidebarInvestmentClickHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {getAmplitudeOfferSelectItemEventProperties} from "../../tracking/amplitude/get_amplitude_offer_select_item_event_properties";
import {IAmplitudeItemListContext} from "../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {getCurrency} from "../../utils/get_currency";
import {getThumbnailLink} from "../../utils/get_thumbnail_link";
import {ViewType} from "../../view_type/ViewType";
import {useOfferLink} from "../detail/hooks/use_offer_link";
import {createOfferListingAmplitudePendingDetailUrl} from "../list/helpers/offer_listing_amplitude_item_list_id";
import {IMapOfferSidebar} from "../map/types/MapOfferSidebar";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {OfferPriceType} from "../types/OfferPriceType";

interface IProps {
    offer: IMapOfferSidebar;
    onOfferSidebarClose: () => void;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    offerIndex?: number;
}

export const OfferSidebarBox = (props: IProps) => {
    const {offer, onOfferSidebarClose, amplitudeItemListContext, offerIndex = 0} = props;
    const offerLink = useOfferLink(props.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: offer.vendor.slug, vendorId: offer.vendor.id});
    const target = useResponsiveLinkTarget();
    const {isMobile} = useUserDevice();

    const onSidebarBoxClick = usePrimaryClick((event: React.MouseEvent<HTMLAnchorElement>) => {
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.INVEST);
        sidebarInvestmentClickHit(ViewType.OFFER_LIST_MAP, offer.id);

        if (amplitudeItemListContext?.itemListId) {
            trackAmplitudeEvent("select_item", {
                ...getAmplitudeOfferSelectItemEventProperties({
                    offer,
                    index: offerIndex,
                    itemListId: amplitudeItemListContext.itemListId,
                    itemListName: amplitudeItemListContext.itemListName
                }),
                location: "listing_map"
            });
        }

        event.currentTarget.href = createOfferListingAmplitudePendingDetailUrl({
            url: event.currentTarget.href,
            itemId: offer.id,
            itemListContext: amplitudeItemListContext
        });
    });

    const onVendorClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();

        window.open(vendorLink, target);
    };
    const offerRangePrice = props.offer.stats?.ranges_price_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    return (
        <>
            <div className={closeHolderStyle} onClick={onOfferSidebarClose}>
                <CloseIcon size="2" />
            </div>

            <a href={offerLink} onMouseDown={onSidebarBoxClick} className={sidebarOfferLinkStyle} title={offer.name} target={target}>
                <div className={flexRowStyle}>
                    <div className={sideBarImageHeaderStyle}>
                        <Image
                            alt={offer.name}
                            src={getThumbnailLink(offer.main_image_url, 224, 147)}
                            width="243"
                            height="160"
                            loading="lazy"
                            ratio={{xs: "243/160"}}
                        />
                    </div>

                    <div className={detailsWrapperStyle}>
                        <Text as="h2" variant="headline_6" className="bt">
                            {offer.name}
                        </Text>

                        <Text className={linkStyle} mb={2} as="h3" variant="info_txt_1">
                            <span onClick={onVendorClick} className={cx(truncateStyle, "bt")}>
                                {offer.vendor.name}
                            </span>
                        </Text>

                        <div className={flexRowStyle}>
                            <MapMarkerOutlineIcon size="2" className={iconSpacingStyle} />

                            <Text as="div" variant="info_txt_2" className={cx(linkStyle, distanceFromTextStyle)}>
                                <span>
                                    {offer?.region.short_name_reverted}, {offer.street_name} {offer.street_number && offer.street_number}
                                </span>
                            </Text>
                        </div>

                        {!isMobile && (
                            <>
                                {offerRangePrice && (
                                    <Text as="div" variant="info_txt_2" mb={1.5}>
                                        <PriceIcon size="2" className={iconSpacingStyle} />
                                        Cena:{" "}
                                        <Text as="span" variant="info_txt_2" strong>
                                            od{" "}
                                            <span className="bt">
                                                {numberFormat(offerRangePrice)} {getCurrency({country: offer.region.country})}
                                                {priceType}
                                            </span>
                                        </Text>
                                    </Text>
                                )}
                                <Text as="div" variant="info_txt_2" mb={1.5}>
                                    <CalendarCheckIcon size="2" className={iconSpacingStyle} />
                                    Termin oddania:{" "}
                                    <Text as="span" variant="info_txt_2" strong>
                                        {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarterLong)}
                                    </Text>
                                </Text>
                            </>
                        )}
                    </div>
                </div>
                {isMobile && (
                    <div className={offerDetailHolderStyle}>
                        <div>
                            {offerRangePrice && (
                                <div className={detailRowStyle}>
                                    <PriceIcon size="2" />

                                    <Text as="div" variant="info_txt_2" className={detailRowTextStyle}>
                                        Cena{" "}
                                        <Text as="span" variant="info_txt_2" strong>
                                            od:
                                            {numberFormat(offerRangePrice)} {getCurrency({country: offer.region.country})}
                                        </Text>
                                    </Text>
                                </div>
                            )}

                            <div className={detailRowStyle}>
                                <CalendarCheckIcon size="2" />
                                <Text as="div" variant="info_txt_2" className={detailRowTextStyle}>
                                    Termin oddania:{" "}
                                    <Text as="span" variant="info_txt_2" strong>
                                        {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarterLong)}
                                    </Text>
                                </Text>
                            </div>
                        </div>
                    </div>
                )}
            </a>
        </>
    );
};

const sidebarOfferLinkStyle = css({
    display: "block",
    pt: 3,
    pr: 3,
    pb: 0,
    pl: 3,
    _hover: {
        color: "inherit",
        textDecoration: "none"
    },
    _focus: {
        color: "inherit",
        textDecoration: "none"
    },
    md: {
        pt: 0,
        pr: 0,
        pb: 0,
        pl: 0
    }
});

const flexRowStyle = css({
    display: "flex"
});

const sideBarImageHeaderStyle = css({
    position: "relative",
    width: "104px",
    flexShrink: 0,
    marginRight: 1,
    md: {
        width: "243px",
        marginRight: 2
    }
});

const detailsWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    xs: {
        width: "50%"
    }
});

const linkStyle = css({
    color: "var(--colors-gray-700)",
    _hover: {
        color: "inherit"
    },
    _focus: {
        color: "inherit"
    }
});

const truncateStyle = css({
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
});

const iconSpacingStyle = css({
    marginRight: 1
});

const distanceFromTextStyle = css({
    width: "100%",
    marginBottom: 0,
    md: {
        marginBottom: "2.8rem"
    }
});

const offerDetailHolderStyle = css({
    display: "flex",
    minHeight: "7.2rem",
    justifyContent: "space-between",
    paddingTop: 2,
    xs: {
        paddingTop: "3rem"
    }
});

const detailRowStyle = css({
    display: "flex",
    alignItems: "center",
    paddingBottom: 2
});

const detailRowTextStyle = css({
    paddingLeft: 2
});

const closeHolderStyle = css({
    cursor: "pointer",
    position: "absolute",
    right: "2rem",
    top: 0,
    xs: {
        top: "2rem"
    }
});
import React from "react";
import {css, cx} from "@panda/css";

import {CalendarCheckIcon, CloseIcon, MapMarkerOutlineIcon, PriceIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {rpAppLink} from "@pg-mono/rp-routes";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {usePrimaryClick} from "../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {Country} from "../../region/types/Country";
import {sidebarInvestmentClickHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {getAmplitudeOfferSelectItemEventProperties} from "../../tracking/amplitude/get_amplitude_offer_select_item_event_properties";
import {IAmplitudeItemListContext} from "../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {getCurrency} from "../../utils/get_currency";
import {getThumbnailLink} from "../../utils/get_thumbnail_link";
import {ViewType} from "../../view_type/ViewType";
import {useOfferLink} from "../detail/hooks/use_offer_link";
import {createOfferListingAmplitudePendingDetailUrl} from "../list/helpers/offer_listing_amplitude_item_list_id";
import {IMapOfferSidebar} from "../map/types/MapOfferSidebar";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {OfferPriceType} from "../types/OfferPriceType";

interface IProps {
    offer: IMapOfferSidebar;
    onOfferSidebarClose: () => void;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    offerIndex?: number;
}

export const OfferSidebarBox = (props: IProps) => {
    const {offer, onOfferSidebarClose, amplitudeItemListContext, offerIndex = 0} = props;
    const offerLink = useOfferLink(props.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: offer.vendor.slug, vendorId: offer.vendor.id});
    const target = useResponsiveLinkTarget();
    const {isMobile} = useUserDevice();

    const onSidebarBoxClick = usePrimaryClick((event: React.MouseEvent<HTMLAnchorElement>) => {
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.INVEST);
        sidebarInvestmentClickHit(ViewType.OFFER_LIST_MAP, offer.id);

        if (amplitudeItemListContext?.itemListId) {
            trackAmplitudeEvent("select_item", {
                ...getAmplitudeOfferSelectItemEventProperties({
                    offer,
                    index: offerIndex,
                    itemListId: amplitudeItemListContext.itemListId,
                    itemListName: amplitudeItemListContext.itemListName
                }),
                location: "listing_map"
            });
        }

        event.currentTarget.href = createOfferListingAmplitudePendingDetailUrl({
            url: event.currentTarget.href,
            itemId: offer.id,
            itemListContext: amplitudeItemListContext
        });
    });

    const onVendorClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();

        window.open(vendorLink, target);
    };
    const offerRangePrice = props.offer.stats?.ranges_price_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    return (
        <>
            <div className={closeHolderStyle} onClick={onOfferSidebarClose}>
                <CloseIcon size="2" />
            </div>

            <a href={offerLink} onMouseDown={onSidebarBoxClick} className={sidebarOfferLinkStyle} title={offer.name} target={target}>
                <div className={flexRowStyle}>
                    <div className={sideBarImageHeaderStyle}>
                        <Image
                            alt={offer.name}
                            src={getThumbnailLink(offer.main_image_url, 224, 147)}
                            width="243"
                            height="160"
                            loading="lazy"
                            ratio={{xs: "243/160"}}
                        />
                    </div>

                    <div className={detailsWrapperStyle}>
                        <Text as="h2" variant="headline_6" className="bt">
                            {offer.name}
                        </Text>

                        <Text className={linkStyle} mb={2} as="h3" variant="info_txt_1">
                            <span onClick={onVendorClick} className={cx(truncateStyle, "bt")}>
                                {offer.vendor.name}
                            </span>
                        </Text>

                        <div className={flexRowStyle}>
                            <MapMarkerOutlineIcon size="2" className={iconSpacingStyle} />

                            <Text as="div" variant="info_txt_2" className={cx(linkStyle, distanceFromTextStyle)}>
                                <span>
                                    {offer?.region.short_name_reverted}, {offer.street_name} {offer.street_number && offer.street_number}
                                </span>
                            </Text>
                        </div>

                        {!isMobile && (
                            <>
                                {offerRangePrice && (
                                    <Text as="div" variant="info_txt_2" mb={1.5}>
                                        <PriceIcon size="2" className={iconSpacingStyle} />
                                        Cena:{" "}
                                        <Text as="span" variant="info_txt_2" strong>
                                            od{" "}
                                            <span className="bt">
                                                {numberFormat(offerRangePrice)} {getCurrency({country: offer.region.country})}
                                                {priceType}
                                            </span>
                                        </Text>
                                    </Text>
                                )}
                                <Text as="div" variant="info_txt_2" mb={1.5}>
                                    <CalendarCheckIcon size="2" className={iconSpacingStyle} />
                                    Termin oddania:{" "}
                                    <Text as="span" variant="info_txt_2" strong>
                                        {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarterLong)}
                                    </Text>
                                </Text>
                            </>
                        )}
                    </div>
                </div>
                {isMobile && (
                    <div className={offerDetailHolderStyle}>
                        <div>
                            {offerRangePrice && (
                                <div className={detailRowStyle}>
                                    <PriceIcon size="2" />

                                    <Text as="div" variant="info_txt_2" className={detailRowTextStyle}>
                                        Cena{" "}
                                        <Text as="span" variant="info_txt_2" strong>
                                            od:
                                            {numberFormat(offerRangePrice)} {getCurrency({country: offer.region.country})}
                                        </Text>
                                    </Text>
                                </div>
                            )}

                            <div className={detailRowStyle}>
                                <CalendarCheckIcon size="2" />
                                <Text as="div" variant="info_txt_2" className={detailRowTextStyle}>
                                    Termin oddania:{" "}
                                    <Text as="span" variant="info_txt_2" strong>
                                        {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarterLong)}
                                    </Text>
                                </Text>
                            </div>
                        </div>
                    </div>
                )}
            </a>
        </>
    );
};

const sidebarOfferLinkStyle = css({
    display: "block",
    pt: 3,
    pr: 3,
    pb: 0,
    pl: 3,
    _hover: {
        color: "inherit",
        textDecoration: "none"
    },
    _focus: {
        color: "inherit",
        textDecoration: "none"
    },
    md: {
        pt: 0,
        pr: 0,
        pb: 0,
        pl: 0
    }
});

const flexRowStyle = css({
    display: "flex"
});

const sideBarImageHeaderStyle = css({
    position: "relative",
    width: "104px",
    flexShrink: 0,
    marginRight: 1,
    md: {
        width: "243px",
        marginRight: 2
    }
});

const detailsWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    xs: {
        width: "50%"
    }
});

const linkStyle = css({
    color: "var(--colors-gray-700)",
    _hover: {
        color: "inherit"
    },
    _focus: {
        color: "inherit"
    }
});

const truncateStyle = css({
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
});

const iconSpacingStyle = css({
    marginRight: 1
});

const distanceFromTextStyle = css({
    width: "100%",
    marginBottom: 0,
    md: {
        marginBottom: "2.8rem"
    }
});

const offerDetailHolderStyle = css({
    display: "flex",
    minHeight: "7.2rem",
    justifyContent: "space-between",
    paddingTop: 2,
    xs: {
        paddingTop: "3rem"
    }
});

const detailRowStyle = css({
    display: "flex",
    alignItems: "center",
    paddingBottom: 2
});

const detailRowTextStyle = css({
    paddingLeft: 2
});

const closeHolderStyle = css({
    cursor: "pointer",
    position: "absolute",
    right: "2rem",
    top: 0,
    xs: {
        top: "2rem"
    }
});
